/**
 * @fileoverview Provide a shared dialog boundary around caller-owned workflow content. Visibility and dismissal decisions belong to the parent; the dialog owns its portal, keyboard focus containment, and temporary document-scroll interaction. Escape, backdrop, and close controls request the same parent dismissal. Saving or cancelling business work belongs to the enclosing workflow.
 */

import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import './AppModal.css';
import AppIcon from "./AppIcon";

/**
 * onClose requests parent-owned dismissal; it does not save or roll back domain work. Visible dialogs temporarily own body-scroll locking, and teardown restores the prior style. Callers must decide what dismissal means for pending actions.
 */
function AppModal({ visible, title, description, onClose, footer, children, width, className,
  style, contentStyle, closable = true, dismissableMask = true, closeOnEscape = true }) {
  const dialogRef = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(

  () => {
    if (!visible) return;

    const previousOverflow = document.body.style.overflow;
    const previousFocus = document.activeElement;
    const dialog = dialogRef.current;
    const focusable = () => [...(dialog?.querySelectorAll('button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], [tabindex="0"]') || [])];
    const handleKey =

    (e) => {
      const dialogs = document.querySelectorAll('[role="dialog"]');
      if (dialogs[dialogs.length - 1] !== dialog) return;
      if (e.key === 'Escape' && closeOnEscape && closable) closeRef.current?.();
      if (e.key === 'Tab') {
        const items = focusable();
        const first = items[0];
        const last = items[items.length - 1];
        if (!first) { e.preventDefault(); dialog?.focus(); }
        else if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };

    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKey);
    (focusable()[0] || dialog)?.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKey);
      previousFocus?.focus?.();
    };
  }, [visible, closeOnEscape, closable]);

  if (!visible || typeof document === 'undefined') return null;

  const dialogStyle = width ?
  { '--app-modal-width': typeof width === 'number' ? `${width}px` : width } :
  undefined;

  return createPortal(
    <div className="app-modal-backdrop" onClick={dismissableMask ? onClose : undefined}>
            <div
        role="dialog"
        ref={dialogRef}
        tabIndex={-1}
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
        className={`app-modal${className ? ` ${className}` : ''}`}
        style={{ ...dialogStyle, ...style }}
        onClick={(e) => e.stopPropagation()}>

                <div className="app-modal__header">
                    <div className="app-modal__header-copy">
                        <h2 className="app-modal__title">{title}</h2>
                        {description &&
            <p className="app-modal__description">{description}</p>}

                    </div>
                    {closable && <button
            type="button"
            onClick={onClose}
            aria-label="Close dialog"
            className="app-modal__close">

                        <AppIcon icon="pi pi-times" aria-hidden="true" />
                    </button>}
                </div>
                <div className="app-modal__body" style={contentStyle}>{children}</div>
                {footer && <div className="app-modal__footer">{footer}</div>}
            </div>
        </div>,
    document.body
  );
}

export default AppModal;
